// ===== ÉTAT D'UN HOLOGRAMME (clignotement) =====
// Sous-objet <préfixe>HologramState, au même niveau que la clé <préfixe>Hologram :
//   {
//     "blinkShape": "Off" | "Fixed" | "Square" | "Sine" | "Triangle" | "Sawtooth",
//     "blinkSpeed": <nombre > 0>,            // forcé à 1 pour "Off" et "Fixed"
//     "blinkColorRGB": { "x": r, "y": g, "z": b }   // vector3, composantes flottantes 0-1
//   }
// "Fixed" = couleur émissive constante, sans clignotement.
// Préfixes : topic, quiz, step, action (objets) et main (process).

export const BLINK_SHAPES = ["Off", "Fixed", "Square", "Sine", "Triangle", "Sawtooth"];
export const DEFAULT_BLINK_SHAPE = "Off";
export const FIXED_BLINK_SHAPE = "Fixed";
export const DEFAULT_BLINK_SPEED = 1;
export const COLOR_DECIMALS = 3;

export const BLINK_SPEED_ERROR =
  "Veuillez saisir une vitesse de clignotement numérique strictement positive (ex : 1 ou 1,5).";

/*** Couleur par défaut : blanc (nouvel objet à chaque appel) */
export function getDefaultBlinkColor() {
  return { x: 1, y: 1, z: 1 };
}

/*** État par défaut : pas de clignotement, vitesse par défaut, couleur blanche */
export function getDefaultHologramState() {
  return {
    blinkShape: DEFAULT_BLINK_SHAPE,
    blinkSpeed: DEFAULT_BLINK_SPEED,
    blinkColorRGB: getDefaultBlinkColor(),
  };
}

const roundColor = (value) => {
  const factor = 10 ** COLOR_DECIMALS;
  return Math.round(value * factor) / factor;
};

/**
 * Retourne toujours un NOUVEL objet { x, y, z } valide (composantes numériques de 0 à 1,
 * arrondies à 3 décimales). Absent ou invalide (texte, composante manquante ou hors 0-1)
 * => couleur par défaut.
 */
export function normalizeBlinkColor(raw) {
  const components = ["x", "y", "z"].map((key) => raw?.[key]);
  const valid = components.every((c) => typeof c === "number" && Number.isFinite(c) && c >= 0 && c <= 1);
  if (!valid) return getDefaultBlinkColor();
  return { x: roundColor(components[0]), y: roundColor(components[1]), z: roundColor(components[2]) };
}

/*** "#rrggbb" (valeur d'un <input type="color">) => { x, y, z } en 0-1, arrondi à 3 décimales */
export function hexToColorVector(hex) {
  const match = typeof hex === "string" ? /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim()) : null;
  if (!match) return getDefaultBlinkColor();
  const [x, y, z] = match.slice(1).map((c) => roundColor(parseInt(c, 16) / 255));
  return { x, y, z };
}

/*** { x, y, z } en 0-1 => "#rrggbb" (couleur invalide => blanc) */
export function colorVectorToHex(vector) {
  const { x, y, z } = normalizeBlinkColor(vector);
  return "#" + [x, y, z].map((c) => Math.round(c * 255).toString(16).padStart(2, "0")).join("");
}

/**
 * Convertit une vitesse saisie en nombre (virgule ou point accepté).
 * Retourne null si la valeur n'est pas un nombre fini (chaîne vide, texte...).
 */
export function parseBlinkSpeed(val) {
  if (typeof val === "number") return Number.isFinite(val) ? val : null;
  if (typeof val !== "string") return null;
  const text = val.trim().replace(",", ".");
  if (text === "") return null;
  const num = Number(text);
  return Number.isFinite(num) ? num : null;
}

/**
 * Retourne toujours un NOUVEL objet { blinkShape, blinkSpeed, blinkColorRGB } complet et valide.
 * - forme absente ou inconnue => "Off"
 * - "Off" => vitesse et couleur par défaut
 * - "Fixed" => vitesse forcée à 1, couleur conservée
 * - autre forme : vitesse absente, invalide ou <= 0 => vitesse par défaut
 * - couleur absente ou invalide => blanc (ancien JSON sans blinkColorRGB compris)
 */
export function normalizeHologramState(raw) {
  const shape = BLINK_SHAPES.includes(raw?.blinkShape) ? raw.blinkShape : DEFAULT_BLINK_SHAPE;
  if (shape === DEFAULT_BLINK_SHAPE) return getDefaultHologramState();
  const blinkColorRGB = normalizeBlinkColor(raw?.blinkColorRGB);
  if (shape === FIXED_BLINK_SHAPE) return { blinkShape: shape, blinkSpeed: DEFAULT_BLINK_SPEED, blinkColorRGB };
  const speed = parseBlinkSpeed(raw?.blinkSpeed);
  return { blinkShape: shape, blinkSpeed: speed !== null && speed > 0 ? speed : DEFAULT_BLINK_SPEED, blinkColorRGB };
}

/*** Validation avant sauvegarde : la vitesse n'est contrôlée que pour un vrai clignotement (ni Off ni Fixed) */
export function validateHologramState(state) {
  const shape = BLINK_SHAPES.includes(state?.blinkShape) ? state.blinkShape : DEFAULT_BLINK_SHAPE;
  if (shape === DEFAULT_BLINK_SHAPE || shape === FIXED_BLINK_SHAPE) return { isValid: true, error: null };
  const speed = parseBlinkSpeed(state?.blinkSpeed);
  if (speed === null || speed <= 0) return { isValid: false, error: BLINK_SPEED_ERROR };
  return { isValid: true, error: null };
}

/**
 * Retourne une copie de `obj` avec `<prefix>HologramState` normalisé.
 * Si la clé existe déjà elle garde sa place, sinon elle est insérée juste après
 * `<prefix>HologramInteractable` (ou `<prefix>Hologram`), ou à la fin.
 */
export function withHologramState(obj, prefix) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  const stateKey = `${prefix}HologramState`;
  const state = normalizeHologramState(obj[stateKey]);
  if (stateKey in obj) return { ...obj, [stateKey]: state };

  const anchor = [`${prefix}HologramInteractable`, `${prefix}Hologram`].find((k) => k in obj);
  if (!anchor) return { ...obj, [stateKey]: state };

  const result = {};
  Object.keys(obj).forEach((key) => {
    result[key] = obj[key];
    if (key === anchor) result[stateKey] = state;
  });
  return result;
}

/**
 * Normalise tous les sous-objets <préfixe>HologramState d'un JSON complet (Learn ou Work) :
 * topics, questions, steps, actions et process (mainHologramState).
 * Ne modifie pas l'objet d'origine.
 */
export function normalizeHologramStatesInJson(json) {
  if (!json || typeof json !== "object" || !json.process || typeof json.process !== "object") return json;
  const process = json.process;
  let newProcess = { ...process };

  if (Array.isArray(process.topics)) {
    newProcess.topics = process.topics.map((topic) => {
      const withState = withHologramState(topic, "topic");
      if (!Array.isArray(withState?.quizzes)) return withState;
      return { ...withState, quizzes: withState.quizzes.map((quiz) => withHologramState(quiz, "quiz")) };
    });
  }

  if (Array.isArray(process.steps)) {
    newProcess.steps = process.steps.map((step) => {
      const withState = withHologramState(step, "step");
      if (!Array.isArray(withState?.stepActions)) return withState;
      return { ...withState, stepActions: withState.stepActions.map((action) => withHologramState(action, "action")) };
    });
  }

  // process : uniquement pour les JSON de procédure (steps) ou ceux qui ont déjà un mainHologram
  if (Array.isArray(process.steps) || "mainHologram" in process) {
    newProcess = withHologramState(newProcess, "main");
  }

  return { ...json, process: newProcess };
}
